import { useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { createRoom } from '../services/roomApi';
import Modal from './ui/Modal';
import ToggleSwitch from './ui/ToggleSwitch';
import ErrorMessage from './ui/ErrorMessage';
import useCopyToClipboard from '../hooks/useCopyToClipboard';
import {
  containerVariants, itemVariants, headerVariants,
} from '../utils/animations';

function CreateRoomModal({ puzzle, onClose }) {
  const [name, setName] = useState(puzzle.title || 'Puzzle Room');
  const [expiresInDays, setExpiresInDays] = useState(7); // Default to 1 week (max allowed)
  const [allowGuests, setAllowGuests] = useState(true);
  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');
  const [createdRoom, setCreatedRoom] = useState(null);

  const { copied, copyToClipboard } = useCopyToClipboard();
  const { copied: shareCopied, copyToClipboard: copyShareText } = useCopyToClipboard();

  const handleCreate = async () => {
    try {
      setIsCreating(true);
      setError('');

      const room = await createRoom({
        // eslint-disable-next-line no-underscore-dangle
        puzzleId: puzzle._id,
        name,
        expiresInDays,
        allowGuests,
        showLeaderboard,
      });

      setCreatedRoom(room);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleCopyLink = () => {
    const url = `${window.location.origin}/room/${createdRoom.inviteCode}`;
    copyToClipboard(url);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/room/${createdRoom.inviteCode}`;
    const shareText = `You've been invited to complete a puzzle! 🧩

${url}`;

    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Puzzle Invitation',
          text: shareText,
        });
      } else {
        copyShareText(shareText);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        copyShareText(shareText);
      }
    }
  };

  const handleSkip = () => {
    onClose(false);
  };

  const handleDone = () => {
    onClose(true);
  };

  // Show success state after room is created
  if (createdRoom) {
    const roomUrl = `${window.location.origin}/room/${createdRoom.inviteCode}`;

    return (
      <Modal onClose={handleDone}>
        <div className="text-center">
          <div className="text-5xl mb-4">🎉</div>
          <h2 className="text-xl font-semibold text-puzzle-text mb-2">
            Room Created!
          </h2>
          <p className="text-puzzle-text-secondary mb-6">
            Share this link with friends to play together:
          </p>

          {/* Link display */}
          <div className="bg-puzzle-background rounded-lg p-3 mb-4 break-all">
            <code className="text-sm text-puzzle-blue">{roomUrl}</code>
          </div>

          {/* Action buttons */}
          <div className="flex gap-3 mb-3">
            <button
              type="button"
              onClick={handleShare}
              className="flex-1 py-3 px-4 bg-puzzle-green text-white font-medium rounded-full hover:bg-green-600 transition-colors"
            >
              {shareCopied ? '✓ Copied!' : 'Share'}
            </button>
            <button
              type="button"
              onClick={handleCopyLink}
              className="flex-1 py-3 px-4 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors"
            >
              {copied ? '✓ Copied!' : 'Copy Link'}
            </button>
          </div>

          {/* Done button */}
          <button
            type="button"
            onClick={handleDone}
            className="w-full py-3 px-4 bg-puzzle-muted text-puzzle-text font-medium rounded-full hover:bg-puzzle-border transition-colors"
          >
            Start Playing
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal onClose={() => onClose(false)}>
      {/* Header */}
      <motion.div
        className="flex justify-between items-center mb-6"
        variants={headerVariants}
        initial="hidden"
        animate="visible"
      >
        <div>
          <h2 className="text-xl font-semibold text-puzzle-text">Share with Friends?</h2>
          <p className="text-sm text-puzzle-text-secondary mt-1">
            Create a room to compete with others
          </p>
        </div>
        <button
          onClick={() => onClose(false)}
          type="button"
          className="p-1 text-puzzle-text-secondary hover:text-puzzle-text transition-colors"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </motion.div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Form */}
        <motion.div className="space-y-4" variants={itemVariants}>
          {/* Room name */}
          <div>
            <label htmlFor="room-name" className="block text-sm font-medium text-puzzle-text mb-1">
              Room Name
              <input
                type="text"
                id="room-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue bg-puzzle-surface text-puzzle-text"
              />
            </label>
          </div>

          {/* Expiration */}
          <div>
            <label htmlFor="expires" className="block text-sm font-medium text-puzzle-text mb-1">
              Expires After
              <select
                id="expires"
                value={expiresInDays}
                onChange={(e) => setExpiresInDays(Number(e.target.value))}
                className="mt-1 w-full px-3 py-2 border border-puzzle-border rounded-lg focus:outline-none focus:ring-2 focus:ring-puzzle-blue bg-puzzle-surface text-puzzle-text"
              >
                <option value={1}>1 day</option>
                <option value={7}>1 week</option>
              </select>
            </label>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm text-puzzle-text">Allow guests to play</span>
              <ToggleSwitch
                enabled={allowGuests}
                onToggle={() => setAllowGuests(!allowGuests)}
                label="Allow guests to play"
              />
            </div>

            <div className="flex items-center justify-between">
              <span className="text-sm text-puzzle-text">Show leaderboard</span>
              <ToggleSwitch
                enabled={showLeaderboard}
                onToggle={() => setShowLeaderboard(!showLeaderboard)}
                label="Show leaderboard"
              />
            </div>
          </div>
        </motion.div>

        {error && (
        <motion.div className="mt-4" variants={itemVariants}>
          <ErrorMessage message={error} />
        </motion.div>
        )}

        {/* Actions */}
        <motion.div className="mt-6 space-y-3" variants={itemVariants}>
          <motion.button
            type="button"
            onClick={handleCreate}
            disabled={isCreating || !name.trim()}
            className="w-full py-3 px-4 bg-puzzle-blue text-white font-medium rounded-full hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isCreating ? 'Creating...' : 'Create Room & Get Link'}
          </motion.button>

          <motion.button
            type="button"
            onClick={handleSkip}
            className="w-full py-3 px-4 text-puzzle-text-secondary font-medium hover:text-puzzle-text transition-colors"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            Skip, just play solo
          </motion.button>
        </motion.div>
      </motion.div>
    </Modal>
  );
}

CreateRoomModal.propTypes = {
  puzzle: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
};

export default CreateRoomModal;
